import { useState } from 'react';
import { Rating, Box, Typography } from '@mui/material';

const HOVER_LABELS: Record<number, string> = {
  1: 'I should have done it myself',
  2: 'This was poorly done',
  3: 'It gets the job done',
  4: 'This will do just fine',
  5: 'This is exactly what I needed',
};

interface StarRatingProps {
  value: number | null;
  onRate?: (rating: number) => Promise<void>;
  compact?: boolean;
  readOnly?: boolean;
}

export const StarRating = ({ value, onRate, compact, readOnly }: StarRatingProps) => {
  const [hover, setHover] = useState(-1);
  const [submitting, setSubmitting] = useState(false);

  const isReadOnly = readOnly || !onRate;

  const handleChange = async (_event: React.SyntheticEvent, newValue: number | null) => {
    if (!onRate || !newValue || submitting) return;
    setSubmitting(true);
    try {
      await onRate(newValue);
    } finally {
      setSubmitting(false);
    }
  };

  const hoverLabel = hover !== -1 ? HOVER_LABELS[hover] : (value ? HOVER_LABELS[value] : null);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Rating
        value={value}
        onChange={handleChange}
        onChangeActive={(_event, newHover) => setHover(newHover)}
        readOnly={isReadOnly}
        disabled={submitting}
        size={compact ? 'small' : 'medium'}
        sx={{
          '& .MuiRating-iconFilled': {
            color: 'warning.main',
          },
          '& .MuiRating-iconHover': {
            color: 'warning.light',
          },
        }}
      />
      {!compact && hoverLabel && (
        <Typography variant="body2" color="text.secondary">
          {hoverLabel}
        </Typography>
      )}
    </Box>
  );
};
